import { useState } from 'react'
import styled from 'styled-components'
import { Image } from '@/features/shared/elements/Image/NextImage'
import { PositiveIcon } from '@shipt/design-system-icons'
import { getCouponDetails } from '@/features/shop/utils/normalizeCoupon'
import { getRetailerCouponUrl, routes } from '@shared/constants/routes'
import { screenSizes } from '@shipt/design-system-themes'
import { keyframes } from '@/features/shared/styles/effects'
import { Column, Row } from '@shipt/design-system-layouts'
import { Body } from '@shipt/design-system-typography'
import { Button, TextButton } from '@shipt/design-system-buttons'
import {
  useClipCouponAndCalculateSavings,
  useCouponSavings,
} from '@/features/shop/services/Coupons/hooks'
import {
  type CouponPromoCardProps,
  getCouponPromoCardProperties,
} from '@/features/shared/analytics/promoCard'
import { ScrollIntoView } from '@/features/shared/components/ScrollIntoView'
import { type Coupon } from '@/features/shop/services/Coupons/types'
import { ButtonLink } from '@/features/shared/elements/Link/Buttons'
import { useHandleDialog } from '@/features/shared/state/Dialog/useHandleDialog'
import { DynamicCouponModal } from '@/features/shop/components/Modal/CouponModal/DynamicCouponModal'
import { DynamicCouponTermsModal } from '@/features/shop/components/Modal/CouponTermsModal/DynamicCouponTermsModal'
import { useCMSTrackingEvents } from '@/features/cms/components/CMS/CMSContext'
import { useIsGuestUser, useUserStoreName } from '@/features/account/services/User/hooks'
import { usePathWithSearch } from '@/features/shared/hooks/routes/usePathWithSearch'
import { useSegwayRouter } from '@/features/shared/hooks/useSegwayRouter'

type Props = { coupon: Coupon; index: number; shelfIndex?: number }

export const CouponCard = ({ coupon, index, shelfIndex }: Props) => {
  const storeName = useUserStoreName()
  const { trackCMSPromoCardViewed, trackCMSPromoCardClicked } = useCMSTrackingEvents()
  const { couponId, description = '', couponImage = '' } = coupon
  const { isFulfilled, isClipped } = useCouponSavings(coupon)
  const { formattedValue, formattedSummary, hasMinimumQtyReq } = getCouponDetails(coupon)
  const summaryText = hasMinimumQtyReq ? formattedSummary : `Save ${formattedValue}`
  const isViewOnly = description.length >= 130 && !isClipped
  const { clipCouponAndCalculateSavings, isError } = useClipCouponAndCalculateSavings()
  const { openDialog } = useHandleDialog()
  const isGuestUser = useIsGuestUser()
  const exactPath = usePathWithSearch()
  const encodedFullPath = encodeURIComponent(exactPath)
  const router = useSegwayRouter()

  const [isInClippedState, setIsInClippedState] = useState(() => isClipped)
  const couponProperties = getCouponPromoCardProperties({
    coupon,
    listIndex: index,
    shelfIndex,
  })

  const handlePromoCardClick = (actionType: CouponPromoCardProps['actionType']) =>
    trackCMSPromoCardClicked({ ...couponProperties, action_type: actionType })

  const handleOpenTermsModal = () => {
    handlePromoCardClick('coupon_terms')
    openDialog(DynamicCouponTermsModal, { coupon })
  }
  const handleOnInView = () => trackCMSPromoCardViewed(couponProperties)

  const couponDetailUrl = getRetailerCouponUrl(storeName, couponId)
  const handleClipButtonClick = async () => {
    if (isGuestUser) {
      router.push(`${routes.SIGN_UP.url}?next=${encodedFullPath}`)
      return
    }
    if (isViewOnly) {
      handlePromoCardClick('coupon')
      return openDialog(DynamicCouponModal, { coupon })
    }
    try {
      await clipCouponAndCalculateSavings(coupon, { listIndex: index })
      setTimeout(() => {
        setIsInClippedState(true)
      }, 1000)
    } catch {
      return
    }
  }

  return (
    <Wrapper isError={isError} isClipped={isClipped} isInClippedState={isInClippedState}>
      <CouponScrollIntoView onInView={handleOnInView}>
        <ImageWrapper align="start" justify="center">
          <Image
            src={couponImage}
            alt={`Image for ${description}`}
            fill
            sizes="88px"
            style={{ objectFit: 'contain' }}
          />
        </ImageWrapper>
      </CouponScrollIntoView>
      <Column justify="space-between">
        <div>
          <CouponTitle strong as="h2">
            {isFulfilled ? `-${formattedValue} applied` : summaryText}
          </CouponTitle>
          <CouponDescription variant="secondary" size="sm">
            {description}
          </CouponDescription>
        </div>
        <TextButton onClick={handleOpenTermsModal} aria-label="open to view coupon terms" size="md">
          Coupon terms
        </TextButton>
      </Column>
      <CouponActionWrapper align="center" justify="center">
        {!isInClippedState && (
          <>
            <ClipButton
              onClick={handleClipButtonClick}
              disabled={isClipped}
              data-should-animate={isClipped}
              size="sm"
              variant={isFulfilled ? 'tertiary' : 'primary'}
            >
              {isError ? 'Error' : isViewOnly ? 'View' : 'Clip'}
            </ClipButton>
            {!!isClipped && <ClippedIcon />}
          </>
        )}
        {!!isClipped && (
          <ShopButton
            onClick={() => handlePromoCardClick('shop')}
            href={couponDetailUrl}
            data-should-animate={!isInClippedState}
            aria-label="select to see all eligible items"
            variant={isFulfilled ? 'tertiary' : 'primary'}
            size="sm"
          >
            Shop
          </ShopButton>
        )}
      </CouponActionWrapper>
      {!!isClipped && (
        <FoldedCornerIcon
          data-should-animate={!isInClippedState}
          width="38"
          height="33"
          viewBox="0 0 38 33"
          fill="currentColor"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M7.99988 0H38L-0.00012207 33V8C-0.00012207 3.58172 3.5816 0 7.99988 0Z"
            fill="currentColor"
          />
          <path
            d="M30.0001 33H0L38.0001 0V25C38.0001 29.4183 34.4184 33 30.0001 33Z"
            fill="white"
          />
        </FoldedCornerIcon>
      )}
    </Wrapper>
  )
}

const CouponScrollIntoView = styled(ScrollIntoView)`
  width: auto;
  height: auto;
`

const Wrapper = styled(Row)<{
  isError: boolean
  isClipped: boolean
  isInClippedState: boolean
}>`
  border-color: ${({ isClipped, isError, theme }) => {
    if (isClipped) return theme.color.border.base.neutral.soft
    return isError
      ? theme.color.border.feedback.negative.loud
      : theme.color.border.base.neutral.soft
  }};
  justify-content: space-between;
  position: relative;
  padding: ${({ isClipped }) => (isClipped ? 12 : 11)}px;
  border-width: ${({ isClipped }) => (isClipped ? 1 : 2)}px;
  border-style: ${({ isClipped }) => (isClipped ? 'solid' : 'dashed')};
  border-radius: 8px;
  border-bottom-right-radius: ${(props) => (props.isInClippedState ? 35 : 8)}px;
  transition:
    border-color 0.3s ease,
    border-style 0.3s ease;
  min-width: 345px;
  max-width: 345px;
  margin-right: 16px;
  margin-bottom: 16px;

  @media ${screenSizes.tablet} {
    &:nth-child(even) {
      margin-right: 0;
    }

    width: 100%;
    max-width: 395px;
  }
`
const ImageWrapper = styled(Row)`
  position: relative;
  width: 60px;
  height: 64px;
  margin-right: 12px;

  @media ${screenSizes.tablet} {
    width: 88px;
    height: 78px;
  }
`
const CouponTitle = styled(Body)`
  margin-top: 0;
  margin-bottom: 4px;
  color: ${({ theme }) => theme.color.content.feedback.promotion.primary_on_soft};
`
const CouponDescription = styled(Body)`
  /* stylelint-disable-next-line value-no-vendor-prefix -- -webkit-line-clamp used here is only compatible with -webkit-box so we need to keep using it */
  display: -webkit-box;
  -webkit-line-clamp: 5;
  -webkit-box-orient: vertical;
  overflow: hidden;
  margin-bottom: 0.25rem;
`

const ClippedIcon = styled(PositiveIcon)`
  opacity: 0;
  position: absolute;
  color: ${({ theme }) => theme.color.content.base.neutral.secondary};
  animation: ${keyframes.scaleToShowAndHide} 0.6s ease-in 0.2s forwards;
`
const ClipButton = styled(Button)<{ 'data-should-animate': boolean }>`
  position: absolute;

  &[data-should-animate='true'] {
    animation: ${keyframes.scaleToHide} 0.2s ease-out forwards;
  }
`
const ShopButton = styled(ButtonLink)<{
  'data-should-animate': boolean
}>`
  padding: 0.3125rem 0.9375rem;

  &[data-should-animate='true'] {
    opacity: 0;
    --enter-scale: 0;
    animation: ${keyframes.enter} 0.3s ease-out 0.8s forwards;
  }
`

const FoldedCornerIcon = styled.svg<{
  'data-should-animate': boolean
}>`
  bottom: -1.2px;
  right: -1.2px;
  position: absolute;
  color: ${({ theme }) => theme.color.border.base.neutral.soft};

  &[data-should-animate='true'] {
    opacity: 0;
    animation: ${keyframes.fade} 0s ease 0.8s forwards;
  }
`

const CouponActionWrapper = styled(Row)`
  position: relative;
  height: 26px;
  min-width: 60px;
`
